// Sprint fc-conversation-search (§2.1, §4 row 1) — pure projector + matcher
// for in-conversation search. No fetch, no store read: takes the messages
// array ChatView already holds and a query string, returns matches.
//
// Corpus is `type === 'user' | 'assistant_text'` ONLY (§2.1) — the two
// message types that render as founder-readable words. `tool_use` /
// `tool_result` (developer content, aggregated away in founder mode) and
// `thinking_block` (redacted on Claude Max, nothing to match) are excluded
// by construction, not by a runtime check — case A3.
//
// The query is NEVER compiled into a RegExp (EC-13: `(`, `*`, `[` etc. are
// matched literally via `String.indexOf`). Matching is diacritic- and
// case-insensitive (EC-2: Wispr dictation drops accents) via a per-character
// NFD-normalise + combining-mark-strip + lowercase, with an index map back
// to the original string so highlight offsets stay correct.

import type { Message } from '../../../types';
import { asAssistantText, asUserContent } from '../../../lib/messages';

export type SearchableMessageType = 'user' | 'assistant_text';

const MIN_QUERY_LENGTH = 2; // EC-1 — shorter queries execute no search
const SNIPPET_MAX_LENGTH = 140; // EC-10
const SNIPPET_PADDING = 60; // chars of context per side before trimming to the cap
export const SEARCH_RESULTS_CAP = 20; // §2.3 — "+N más" tail beyond this

export interface SearchMatch {
  id: string;
  type: SearchableMessageType;
  timestamp: string;
  /** ≤140 chars, markdown emphasis markers stripped (EC-14). */
  snippet: string;
  /** Offset into `snippet` where the highlighted match starts; -1 if the
   * post-strip snippet could not re-locate it (defensive — the row still
   * renders, just without a highlight). */
  matchStart: number;
  matchLength: number;
}

export interface SearchOutcome {
  /** Size of the searchable corpus (user + assistant_text) in `messages`,
   * independent of the query — this is the `{n}` the scope line (§2.4)
   * quotes, never the raw loaded-message count. */
  corpusSize: number;
  /** True when the query is below MIN_QUERY_LENGTH — "no search executed"
   * (EC-1), distinct from a real zero-match result (EC-6). */
  queryTooShort: boolean;
  matches: SearchMatch[];
}

function normalizeChar(ch: string): string {
  return ch.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Normalises `text` char-by-char, returning the normalised string plus a
 * map from each normalised-string index back to its source index in `text`
 * (a source char can expand to 0+ normalised chars, e.g. a combining mark
 * strips to nothing). */
function buildNormalized(text: string): { normalized: string; map: number[] } {
  let normalized = '';
  const map: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const n = normalizeChar(text.charAt(i));
    for (const c of n) {
      normalized += c;
      map.push(i);
    }
  }
  return { normalized, map };
}

/** `map[i]` is always populated for `0 <= i < normalized.length` by
 * construction (one push per output char in the loop above) — this
 * accessor documents that invariant once instead of a `!` at every call
 * site. */
function sourceIndexAt(map: number[], i: number): number {
  const v = map[i];
  if (v === undefined) {
    throw new Error('search-messages: normalized index out of range — buildNormalized invariant broken');
  }
  return v;
}

function normalizeString(text: string): string {
  return buildNormalized(text).normalized;
}

/** Strips markdown emphasis/code markers (EC-14: "**bold**" leaking into a
 * snippet). Not a markdown parser — a founder-facing snippet only ever came
 * from a user/assistant text body, never a code block worth preserving. */
function stripEmphasis(text: string): string {
  return text.replace(/[*_`]+/g, '');
}

/** Re-locates `normalizedQuery` inside `snippet` (which has already had
 * emphasis markers stripped, shifting offsets from the source text) so the
 * highlight is always computed against what actually renders. */
function locateHighlight(
  snippet: string,
  normalizedQuery: string,
): { start: number; length: number } {
  const { normalized, map } = buildNormalized(snippet);
  const hit = normalized.indexOf(normalizedQuery);
  if (hit === -1) return { start: -1, length: 0 };
  const start = sourceIndexAt(map, hit);
  const end = sourceIndexAt(map, hit + normalizedQuery.length - 1) + 1;
  return { start, length: end - start };
}

function extractSnippet(
  text: string,
  matchStart: number,
  matchEnd: number,
  normalizedQuery: string,
): { snippet: string; matchStart: number; matchLength: number } {
  let windowStart = Math.max(0, matchStart - SNIPPET_PADDING);
  let windowEnd = Math.min(text.length, matchEnd + SNIPPET_PADDING);
  while (windowEnd - windowStart > SNIPPET_MAX_LENGTH) {
    if (matchStart - windowStart > windowEnd - matchEnd && windowStart < matchStart) {
      windowStart++;
    } else if (windowEnd > matchEnd) {
      windowEnd--;
    } else {
      break;
    }
  }
  const prefix = windowStart > 0 ? '…' : '';
  const suffix = windowEnd < text.length ? '…' : '';
  const raw = prefix + text.slice(windowStart, windowEnd) + suffix;
  const stripped = stripEmphasis(raw);
  const highlight = locateHighlight(stripped, normalizedQuery);
  return { snippet: stripped, matchStart: highlight.start, matchLength: highlight.length };
}

function textOf(m: Message): string {
  return m.type === 'user' ? asUserContent(m).text : asAssistantText(m).text;
}

export function searchMessages(messages: readonly Message[], query: string): SearchOutcome {
  const corpus = messages.filter(
    (m) => m.type === 'user' || m.type === 'assistant_text',
  );

  const trimmedQuery = query.trim();
  if (trimmedQuery.length < MIN_QUERY_LENGTH) {
    return { corpusSize: corpus.length, queryTooShort: true, matches: [] };
  }

  const normalizedQuery = normalizeString(trimmedQuery);
  const matches: SearchMatch[] = [];

  for (const m of corpus) {
    const text = textOf(m);
    if (!text) continue;
    const { normalized, map } = buildNormalized(text);
    const hit = normalized.indexOf(normalizedQuery);
    if (hit === -1) continue;
    const start = sourceIndexAt(map, hit);
    const end = sourceIndexAt(map, hit + normalizedQuery.length - 1) + 1;
    const { snippet, matchStart, matchLength } = extractSnippet(
      text,
      start,
      end,
      normalizedQuery,
    );
    matches.push({
      id: m.id,
      type: m.type as SearchableMessageType,
      timestamp: m.timestamp,
      snippet,
      matchStart,
      matchLength,
    });
  }

  return { corpusSize: corpus.length, queryTooShort: false, matches };
}
